import React from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { rem } from 'polished';
import { Breadcrumbs as MuiBreadcrumbs } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { emphasize, withStyles, Theme } from '@material-ui/core/styles';
import Chip from '@material-ui/core/Chip';

/** `default` = 72px white bar; `sci` = 55px SCI strip (theme via body[data-theme]). */
export type BreadcrumbsChrome = 'default' | 'sci' | 'dark' | 'light';

export interface LabelProps {
  to?: string;
  children: React.ReactNode;
  active?: boolean;
  select?: boolean;
  onclick?: any;
  id?: any;
  chrome?: BreadcrumbsChrome;
}

export interface BreadcrumbsProps {
  children: React.ReactNode;
  /** SCI-style strip: 55px / padding 0 16px / shadow. Theme follows body[data-theme]. */
  chrome?: BreadcrumbsChrome;
  Label?: React.FC<LabelProps>;
}

function isSciStrip(chrome: BreadcrumbsChrome) {
  return chrome === 'sci' || chrome === 'dark' || chrome === 'light';
}

const StyledBreadcrumbSelect = withStyles((theme: Theme) => ({
  root: {
    padding: '0px',
    justifyContent: 'end',
    backgroundColor: 'white',
    height: '40px',
    width: 'max-content',
    borderRadius: '4px',
    color: '#0B2033',
    font: 'normal normal 600 20px/17px Source Sans Pro',
    '&:hover, &:focus': {
      backgroundColor: theme.palette.grey[300]
    },
    '&:active': {
      boxShadow: theme.shadows[1],
      backgroundColor: emphasize(theme.palette.grey[300], 0.12)
    },
    '& .MuiChip-label': {
      height: '24px',
      position: 'relative',
      top: '2px'
    }
  }
}))(Chip) as typeof Chip;

const sciStrip = css`
  gap: ${rem('16px')};
  height: ${rem('55px')};
  padding: 0 ${rem('16px')};
  border-radius: 0;
  -webkit-box-shadow: 1px 2px 6px rgba(0, 0, 0, 0.2);
  box-shadow: 1px 2px 6px rgba(0, 0, 0, 0.2);
`;

const StyledBreadcrumbs = styled(MuiBreadcrumbs)<{ $sci: boolean }>`
  position: relative;
  z-index: 1000;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: 100%;

  ${props =>
    !props.$sci
      ? css`
          box-shadow: ${props.theme.shadows['1']};
          height: ${rem('72px')};
          padding: 0 ${rem('24px')};
          background: #fff;
        `
      : css`
          ${sciStrip}
          background: #ffffff;
          color: #000000;

          body[data-theme='dark'] & {
            background: #0b2033;
            color: #ffffff;
          }
        `}

  .MuiBreadcrumbs-separator {
    margin: 0 ${rem('8px')};
    color: ${props => (props.$sci ? '#94a3b8' : props.theme.colors.S['600'])};
  }

  ${props =>
    props.$sci &&
    css`
      body[data-theme='dark'] & .MuiBreadcrumbs-separator {
        color: rgba(255, 255, 255, 0.35);
      }
    `}

  .MuiBreadcrumbs-ol {
    align-items: center;
    flex-wrap: nowrap;
  }
`;

const StyledSpan = styled.span<{ active: boolean; $sci: boolean }>`
  font-size: ${props => (props.$sci ? rem('15px') : rem('20px'))};
  font-weight: ${props => {
    if (!props.$sci) {
      return props.active ? 400 : 200;
    }
    return props.active ? 600 : 400;
  }};
  color: ${props => {
    if (!props.$sci) {
      return props.active ? props.theme.colors.B['900'] : props.theme.colors.S['800'];
    }
    return props.active ? '#1e293b' : '#64748b';
  }};

  ${props =>
    props.$sci &&
    css`
      body[data-theme='dark'] & {
        color: ${props.active ? '#ffffff' : 'rgba(255, 255, 255, 0.55)'};
      }
    `}
`;

const ChromeContext = React.createContext<{ sci: boolean }>({ sci: false });

function Label({ to, children, active = false, select = false, onclick, id }: LabelProps) {
  const { sci } = React.useContext(ChromeContext);

  if (to) {
    return (
      <Link to={to}>
        <StyledSpan active={active} $sci={sci}>
          {children}
        </StyledSpan>
      </Link>
    );
  }
  if (select) {
    return (
      <StyledBreadcrumbSelect
        id={id}
        label={children}
        deleteIcon={<ExpandMoreIcon />}
        onClick={onclick}
        onDelete={onclick}
      />
    );
  }
  return (
    <StyledSpan active={active} $sci={sci}>
      {children}
    </StyledSpan>
  );
}

export function Breadcrumbs({ children, chrome = 'default' }: BreadcrumbsProps) {
  const sci = isSciStrip(chrome);

  return (
    <ChromeContext.Provider value={{ sci }}>
      <StyledBreadcrumbs $sci={sci}>{children}</StyledBreadcrumbs>
    </ChromeContext.Provider>
  );
}

Breadcrumbs.Label = React.memo(Label);
